/**
 * API сервис для работы с очередью звонков
 */

import authService from './auth.service';
import type {
  QueuedCallApiResponse,
  AddToQueueApiRequest,
  UpdateQueuedCallApiRequest,
  SetCallResultApiRequest,
  SetCallResultApiResponse,
  AllQueuesApiResponse,
  QueueFilters,
} from '../types/call-queue-api.types';

const BASE_URL = '/api/v1/call-queue';

class CallQueueService {
  /**
   * Получить мою очередь
   */
  async getMyQueue(): Promise<QueuedCallApiResponse[]> {
    const response = await authService.fetchWithAuth(`${BASE_URL}/my`);
    if (!response.ok) {
      const error = await this.parseError(response);
      throw new Error(error);
    }
    const data = await response.json();
    // API может вернуть массив напрямую или объект с полем items
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.items)) {
      return data.items;
    }
    // Если пустой объект или null - возвращаем пустой массив
    return [];
  }

  /**
   * Получить очереди всех операторов (supervisor)
   */
  async getAllQueues(filters?: QueueFilters): Promise<AllQueuesApiResponse> {
    const params = new URLSearchParams();
    if (filters?.user_id) params.set('user_id', String(filters.user_id));
    if (filters?.status) params.set('status', filters.status);
    if (filters?.priority) params.set('priority', filters.priority);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));

    const url = params.toString() ? `${BASE_URL}/all?${params}` : `${BASE_URL}/all`;
    const response = await authService.fetchWithAuth(url);
    if (!response.ok) {
      const error = await this.parseError(response);
      throw new Error(error);
    }
    return response.json();
  }

  /**
   * Получить очередь конкретного оператора (supervisor)
   */
  async getUserQueue(userId: number): Promise<QueuedCallApiResponse[]> {
    const response = await authService.fetchWithAuth(`${BASE_URL}/user/${userId}`);
    if (!response.ok) {
      const error = await this.parseError(response);
      throw new Error(error);
    }
    const data = await response.json();
    // API может вернуть массив напрямую или объект с полем items
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.items)) {
      return data.items;
    }
    return [];
  }

  /**
   * Добавить в очередь
   */
  async addToQueue(payload: AddToQueueApiRequest): Promise<QueuedCallApiResponse> {
    const response = await authService.fetchWithAuth(BASE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      // Проверяем на дубликат (409 Conflict)
      if (response.status === 409) {
        const data = await response.json();
        throw new DuplicateCallError(data.call_id, data.existing_id);
      }
      // Проверяем rate limit (429)
      if (response.status === 429) {
        const data = await response.json();
        throw new RateLimitError(data.retry_after);
      }
      const error = await this.parseError(response);
      throw new Error(error);
    }
    return response.json();
  }

  /**
   * Обновить карточку
   */
  async updateCall(callId: string, payload: UpdateQueuedCallApiRequest): Promise<QueuedCallApiResponse> {
    const response = await authService.fetchWithAuth(`${BASE_URL}/${encodeURIComponent(callId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await this.parseError(response);
      throw new Error(error);
    }
    return response.json();
  }

  /**
   * Удалить из очереди
   */
  async removeFromQueue(callId: string): Promise<void> {
    const response = await authService.fetchWithAuth(`${BASE_URL}/${encodeURIComponent(callId)}`, {
      method: 'DELETE',
    });

    if (!response.ok && response.status !== 404) {
      const error = await this.parseError(response);
      throw new Error(error);
    }
  }

  /**
   * Установить результат звонка
   */
  async setResult(callId: string, payload: SetCallResultApiRequest): Promise<SetCallResultApiResponse> {
    const response = await authService.fetchWithAuth(`${BASE_URL}/${encodeURIComponent(callId)}/result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await this.parseError(response);
      throw new Error(error);
    }
    return response.json();
  }

  /**
   * Парсинг ошибки из ответа
   */
  private async parseError(response: Response): Promise<string> {
    try {
      const data = await response.json();
      return data.error || data.detail || data.message || `HTTP Error ${response.status}`;
    } catch {
      return `HTTP Error ${response.status}`;
    }
  }
}

/**
 * Ошибка дубликата звонка в очереди
 */
export class DuplicateCallError extends Error {
  constructor(public callId: string, public existingId?: number) {
    super(`Call ${callId} already exists in queue`);
    this.name = 'DuplicateCallError';
  }
}

/**
 * Ошибка rate limit
 */
export class RateLimitError extends Error {
  constructor(public retryAfter: number) {
    super(`Rate limit exceeded. Retry after ${retryAfter} seconds`);
    this.name = 'RateLimitError';
  }
}

export default new CallQueueService();
